
import React, { useEffect, useState } from "react";
import "../../../../Cart/style/addcart.css";
import "../../../../Cart/style/customermodel.css";
import toast, { Toaster } from "react-hot-toast";
import "react-loading-skeleton/dist/skeleton.css";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../../../ProductDetail/service/productcart.service";
import { getCustomers } from "../../../../Cart/service/Customers.service";
import { setCartCount } from "../../../../Cart/redux/cartSlice";
import { getCartCount } from "../../../../../Component/header/service/CartCount.service";
import { Link } from "react-router-dom";


function AddCartMobile({ sizes = [] }) {
  const dispatch = useDispatch();
  const cartCount = useSelector(
    (state) => Number(state.cart.cartCount) || 0
  );
  const [quantities, setQuantities] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeCartButtons, setActiveCartButtons] = useState({});
  const [customQty, setCustomQty] = useState({});
  const [updateLoadingId, setUpdateLoadingId] = useState(null);
  const [pendingQuantities, setPendingQuantities] = useState({});

  // =========================================================
  // GET CURRENT CUSTOMER FROM SERVER
  // NO LOCAL STORAGE
  // =========================================================
  const getCurrentCustomer = async () => {
    if (selectedCustomer) {
      const customerId =
        selectedCustomer.customerId ||
        selectedCustomer.customer_id ||
        selectedCustomer.id;

      if (customerId) {
        return {
          ...selectedCustomer,
          id: Number(customerId),
        };
      }
    }

    try {
      const cartRes = await getCartCount("fresh", 1, null);
      const cartData = cartRes?.data;

      if (cartData?.customer_id) {
        const customer = {
          id: Number(cartData.customer_id),
        };

        setSelectedCustomer(customer);

        return customer;
      }
    } catch (error) {
      console.error("Server customer check error:", error);
    }

    return null;
  };

  // =========================================================
  // OPEN CUSTOMER MODAL
  // API SEARCH IS HANDLED BELOW
  // =========================================================
  const openCustomerModalForProduct = (product) => {
    setSelectedProduct(product);
    setSearch("");
    setCustomers([]);
    setActiveIndex(-1);
    setShowModal(true);
  };

  // =========================================================
  // CUSTOMER SEARCH API
  // 300ms DEBOUNCE
  // =========================================================
  useEffect(() => {
    if (!showModal) return;

    const searchText = search.trim();

    const timer = setTimeout(async () => {
      try {
        const list = await getCustomers(searchText);

        setCustomers(Array.isArray(list) ? list : []);
        setActiveIndex(-1);
      } catch (error) {
        console.error("Customer search API error:", error);
        setCustomers([]);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [search, showModal]);

  // =========================================================
  // CUSTOM QUANTITY CHANGE
  // =========================================================
  const handleCustomQuantityChange = (item, value) => {
    const productId = item.product_id;

    if (!/^\d*$/.test(value)) {
      return;
    }

    setQuantities((prev) => ({
      ...prev,
      [productId]: value,
    }));

    if (value === "") {
      setPendingQuantities((prev) => ({
        ...prev,
        [productId]: "",
      }));

      return;
    }

    const qty = Number(value);
    const stock = Number(item.avail_stock || 0);

    if (qty > stock) {
      toast.error(`Only ${stock} stock available`);

      setPendingQuantities((prev) => ({
        ...prev,
        [productId]: qty,
      }));

      return;
    }

    setPendingQuantities((prev) => ({
      ...prev,
      [productId]: qty,
    }));
  };

  // =========================================================
  // UPDATE QUANTITY
  // =========================================================
  const handleUpdateQuantity = async (item) => {
    const productId = item.product_id;

    const qty = Number(
      pendingQuantities[productId] ?? quantities[productId]
    );

    const stock = Number(item.avail_stock || 0);

    if (!qty || qty <= 0) {
      toast.error("Please enter quantity");
      return;
    }

    if (qty > stock) {
      toast.error(`Only ${stock} stock available`);
      return;
    }

    setUpdateLoadingId(productId);

    try {
      const customer = await getCurrentCustomer();

      if (!customer) {
        await openCustomerModalForProduct(item);
        return;
      }

      const customerId =
        customer.customerId ||
        customer.customer_id ||
        customer.id;

      if (!customerId) {
        await openCustomerModalForProduct(item);
        return;
      }

      const payload = {
        cart_type: "fresh",
        customer_id: Number(customerId),
        product_listing_id:
          item.product_listing_id || item.product_id,
        qty: qty,
      };

      const res = await addToCart(payload);

      if (res?.success) {
        setSelectedCustomer({
          ...customer,
          id: Number(customerId),
        });

        setQuantities((prev) => ({
          ...prev,
          [productId]: qty,
        }));

        setPendingQuantities((prev) => {
          const updated = { ...prev };
          delete updated[productId];
          return updated;
        });

        setCustomQty((prev) => ({
          ...prev,
          [productId]: true,
        }));

        setActiveCartButtons((prev) => ({
          ...prev,
          [productId]: true,
        }));

        toast.success(res?.message || "Cart updated successfully");
      } else {
        toast.error(res?.message || "Failed to update cart");
      }
    } catch (error) {
      console.error("Update quantity error:", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to update cart"
      );
    } finally {
      setUpdateLoadingId(null);
    }
  };

  // =========================================================
  // QUANTITY CHANGE 1-9
  // =========================================================
  const handleQuantityChange = async (product, quantity) => {
    const qty = Number(quantity);

    if (qty <= 0) return;

    if (qty >= 10) {
      return;
    }

    const stock = Number(product.avail_stock || 0);

    if (qty > stock) {
      toast.error(
        `Only ${product.avail_stock} stock available`
      );
      return;
    }

    const customer = await getCurrentCustomer();

    if (!customer) {
      await openCustomerModalForProduct(product);
      return;
    }

    await handleCustomerSelect(customer, product, qty);
  };

  // =========================================================
  // CART ICON CLICK
  // =========================================================
  const handleAddToCart = async (item) => {
    const productId = item.product_id;

    const qty = Number(
      pendingQuantities[productId] ??
        quantities[productId] ??
        0
    );

    if (qty <= 0) {
      toast.error("Please select quantity");
      return;
    }

    const stock = Number(item.avail_stock || 0);

    if (qty > stock) {
      toast.error(`Only ${stock} stock available`);
      return;
    }

    setLoadingId(productId);

    try {
      const customer = await getCurrentCustomer();

      if (customer) {
        await handleCustomerSelect(
          customer,
          item,
          qty
        );

        return;
      }

      await openCustomerModalForProduct(item);
    } catch (error) {
      console.error(error);

      toast.error("Unable to load customer");
    } finally {
      setLoadingId(null);
    }
  };

  // =========================================================
  // CUSTOMER SELECT
  // =========================================================
  const handleCustomerSelect = async (
    customer,
    product = selectedProduct,
    selectedQty = null
  ) => {
    if (!product) return;

    const productId = product.product_id;

    const customerId =
      customer?.customerId ||
      customer?.customer_id ||
      customer?.id;

    if (!customerId) {
      toast.error("Invalid customer");
      return;
    }

    const qty = Number(
      selectedQty ??
        pendingQuantities[productId] ??
        quantities[productId] ??
        0
    );

    if (qty <= 0) {
      toast.error("Please select quantity");
      return;
    }

    const stock = Number(product.avail_stock || 0);

    if (qty > stock) {
      toast.error(
        `Only ${product.avail_stock} stock available`
      );
      return;
    }

    setSelectedCustomer({
      ...customer,
      id: Number(customerId),
    });

    setLoadingId(productId);

    try {
      const payload = {
        cart_type: "fresh",
        customer_id: Number(customerId),
        product_listing_id:
          product.product_listing_id ||
          product.product_id,
        qty: qty,
      };

      const res = await addToCart(payload);

      if (res?.success) {
        dispatch(
          setCartCount(cartCount + qty)
        );

        setQuantities((prev) => ({
          ...prev,
          [productId]: qty,
        }));

        setPendingQuantities((prev) => {
          const updated = { ...prev };
          delete updated[productId];
          return updated;
        });

        setCustomQty((prev) => ({
          ...prev,
          [productId]: true,
        }));

        setActiveCartButtons((prev) => ({
          ...prev,
          [productId]: true,
        }));

        toast.success(
          res?.message || "Added to Cart"
        );

        setShowModal(false);
        setSelectedProduct(null);
        setSearch("");
        setCustomers([]);
        setActiveIndex(-1);
      } else {
        toast.error(
          res?.message || "Failed"
        );
      }
    } catch (error) {
      console.error("Add cart error:", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to add cart"
      );
    } finally {
      setLoadingId(null);
    }
  };

  // =========================================================
  // API RESULTS DIRECTLY
  // NO CLIENT-SIDE FILTER
  // =========================================================
  const filteredCustomers = customers;

  // =========================================================
  // HIGHLIGHT SEARCH
  // =========================================================
  const highlightText = (
    text = "",
    search = ""
  ) => {
    const searchText = search.trim();

    if (searchText.length < 2) {
      return text;
    }

    const escapedSearch = searchText.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    const regex = new RegExp(
      `(${escapedSearch})`,
      "gi"
    );

    return String(text)
      .split(regex)
      .map((part, index) =>
        part.toLowerCase() ===
        searchText.toLowerCase() ? (
          <span
            key={index}
            style={{
              fontWeight: "700",
              color: "#000",
            }}
          >
            {part}
          </span>
        ) : (
          part
        )
      );
  };


  // =========================================================
  // UI
  // =========================================================
  return (
    <>
      <Toaster position="top-right" />

      <div
        className="TableBoxcart mobileadd"
        style={{ marginTop: 0 }}
      >
        <div className="table-responsive">
          <div className="table-box-content2" style={{overflow:"auto",height:"380px"}}>
            <div className="table-header-fixed">
              <table className="table custom-table table-border table-sm mb-0" style={{fontSize:"12px",border:"1"}}>
                <thead className="bg-secondary">
                  <tr>
                    <th className="text-center" style={{ width: "22%" }}>
                      Size
                    </th>
                    <th className="text-end" style={{ width: "20%",textAlign:"right" }}>
                      MRP
                    </th>
                    <th className="text-center" style={{ width: "18%" }}>
                      Stock
                    </th>
                    <th className="text-center" style={{ width: "25%" }}>
                      Ord. Qty
                    </th>
                    <th className="text-center" style={{ width: "15%" }}>
                      Cart
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sizes.length > 0 ? (
                    sizes.map((item) => (
                      <tr
                        key={item.product_id}
                      >
                        <td style={{textAlign:"center"}}>
                          <Link
                            to="#"
                            className="size-tooltip"
                            onMouseEnter={(e) => {
                              const tooltip =
                                e.currentTarget.querySelector(
                                  ".tooltip-barcode"
                                );

                              const rect =
                                e.currentTarget.getBoundingClientRect();

                              if (tooltip) {
                                tooltip.style.left = `${
                                  rect.left +
                                  rect.width / 2
                                }px`;

                                tooltip.style.top = `${
                                  rect.top - 8
                                }px`;
                              }
                            }}
                            onClick={(e) =>
                              e.preventDefault()
                            }
                          >
                            {item.size}

                            <span className="tooltip-barcode">
                              {item.barcode}
                            </span>
                          </Link>
                        </td>

                        <td>
                          {item.mrp}
                        </td>

                        <td className="text-center">
                          {item.avail_stock}
                        </td>

                        {/* =================================================
                            QUANTITY
                        ================================================= */}
                        <td className="text-center">
                          
                          {!customQty[item.product_id] ? (
                            <select
                              className="form-control form-control-sm text-center select-List"
                              value={
                                quantities[
                                  item.product_id
                                ] ?? 0
                              }
                              style={{
                                width: "100%",
                                fontSize: "13px",
                              }}
                              onChange={(e) => {
                                const value =
                                  e.target.value;

                                if (value === "10+") {
                                  setCustomQty(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        true,
                                    })
                                  );

                                  setQuantities(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        10,
                                    })
                                  );

                                  setPendingQuantities(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        10,
                                    })
                                  );

                                  return;
                                }

                                const qty =
                                  Number(value);

                                setQuantities(
                                  (prev) => ({
                                    ...prev,
                                    [item.product_id]:
                                      qty,
                                  })
                                );

                                if (
                                  qty >= 1 &&
                                  qty <= 9
                                ) {
                                  handleQuantityChange(
                                    item,
                                    qty
                                  );
                                }
                              }}
                            >
                              {Array.from(
                                {
                                  length:
                                    Math.min(
                                      10,
                                      Number(
                                        item.avail_stock
                                      ) + 1
                                    ),
                                },
                                (_, i) => i
                              ).map((qty) => (
                                <option
                                  key={qty}
                                  value={qty}
                                >
                                  {qty}
                                </option>
                              ))}

                              {Number(
                                item.avail_stock
                              ) >= 10 && (
                                <option value="10+">
                                  10+
                                </option>
                              )}
                            </select>
                          ) : (
                            <input
                              type="text"
                              inputMode="numeric"
                              className="form-control form-control-sm text-center select-List"
                              value={
                                quantities[
                                  item.product_id
                                ] ?? ""
                              }
                              autoFocus
                              style={{
                                width: "100%",
                                fontSize: "13px",
                              }}
                              onFocus={(e) =>
                                e.target.select()
                              }
                              onChange={(e) =>
                                handleCustomQuantityChange(
                                  item,
                                  e.target.value
                                )
                              }
                              onKeyDown={(e) => {
                                if (
                                  e.key === "Enter"
                                ) {
                                  e.target.blur();
                                }
                              }}
                              onBlur={() => {
                                const value =
                                  quantities[
                                    item.product_id
                                  ];

                                if (
                                  value === "" ||
                                  value === undefined
                                ) {
                                  setQuantities(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        10,
                                    })
                                  );

                                  setPendingQuantities(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        10,
                                    })
                                  );
                                }
                              }}
                            />
                          )}
                        </td>

                        {/* =================================================
                            CART
                        ================================================= */}
                        <td className="text-center">
                          {(() => {
                            const productId =
                              item.product_id;

                            const quantity =
                              pendingQuantities[
                                productId
                              ] ??
                              quantities[
                                productId
                              ];

                            const hasQuantity =
                              quantity !== undefined &&
                              quantity !== null &&
                              quantity !== "" &&
                              Number(quantity) > 0;

                            const stock =
                              Number(
                                item.avail_stock || 0
                              );

                            const hasPendingUpdate =
                              pendingQuantities[
                                productId
                              ] !== undefined &&
                              pendingQuantities[
                                productId
                              ] !== null &&
                              pendingQuantities[
                                productId
                              ] !== "";

                            const isUpdating =
                              updateLoadingId ===
                              productId;

                            const isAdding =
                              loadingId ===
                              productId;

                            const isOutOfStockQuantity =
                              Number(
                                quantity || 0
                              ) > stock;

                            return (
                              <button
                                type="button"
                                className={`btn btn-sm addtocart ${
                                  hasPendingUpdate
                                    ? "btn-update"
                                    : activeCartButtons[
                                        productId
                                      ]
                                    ? "btn-success"
                                    : "btn-outline-secondary"
                                }`}
                                disabled={
                                  isAdding ||
                                  isUpdating ||
                                  stock <= 0 ||
                                  !hasQuantity ||
                                  isOutOfStockQuantity
                                }
                                onClick={async () => {
                                  if (!hasQuantity) {
                                    toast.error(
                                      "Please select quantity"
                                    );
                                    return;
                                  }

                                  if (
                                    Number(quantity) >
                                    stock
                                  ) {
                                    toast.error(
                                      `Only ${stock} stock available`
                                    );
                                    return;
                                  }

                                  if (
                                    hasPendingUpdate
                                  ) {
                                    await handleUpdateQuantity(
                                      item
                                    );
                                  } else {
                                    await handleAddToCart(
                                      item
                                    );
                                  }
                                }}
                              >
                                {isAdding ||
                                isUpdating ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : hasPendingUpdate ? (
                                  <i className="bi bi-arrow-repeat"></i>
                                ) : (
                                  <i className="bi bi-cart-plus"></i>
                                )}
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-center py-3"
                      >
                        No sizes available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          CUSTOMER MODAL
      ========================================================= */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSearch("");
          setCustomers([]);
          setSelectedProduct(null);
          setActiveIndex(-1);
        }}
        className="model001"
        style={{
          background: "#0000008a",
        }}
      >
        <Modal.Body>
          <div className="position-relative serchlistdata pt-2">
            <input
              type="search"
              className="form-control"
              placeholder="Search Customer..."
              autoFocus
              value={search}
              style={{
                borderRadius: "5px",
                fontSize: "13px",
                height: "30px",
                marginTop: "0",
                width: "85%",
              }}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();

                  setActiveIndex((prev) =>
                    prev < filteredCustomers.length - 1
                      ? prev + 1
                      : 0
                  );
                }

                if (e.key === "ArrowUp") {
                  e.preventDefault();

                  setActiveIndex((prev) =>
                    prev > 0
                      ? prev - 1
                      : filteredCustomers.length - 1
                  );
                }

                if (
                  e.key === "Enter" &&
                  activeIndex >= 0 &&
                  filteredCustomers[activeIndex]
                ) {
                  e.preventDefault();

                  handleCustomerSelect(
                    filteredCustomers[activeIndex]
                  );
                }
              }}
            />

            {search && (
              <div
                className="position-absolute"
                style={{
                  top: "calc(100% - 1px)",
                  left: 0,
                  right: 0,
                  maxHeight: "250px",
                  overflowY: "auto",
                  background: "#fff",
                  borderWidth:
                    "medium 1px 1px",
                  borderStyle:
                    "none solid solid",
                  borderColor:
                    "currentcolor rgb(222, 226, 230) rgb(222, 226, 230)",
                  borderRadius:
                    "4px 3px 6px 6px",
                  boxShadow:
                    "rgba(0, 0, 0, 0.15) 0px 15px 12px",
                  zIndex: 9999,
                  width: "85%",
                  fontSize: "13px",
                }}
              >
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map(
                    (customer, index) => (
                      <div
                        key={
                          customer.id ||
                          customer.customer_id ||
                          `customer-${index}`
                        }
                        onClick={() =>
                          handleCustomerSelect(
                            customer
                          )
                        }
                        onMouseEnter={() =>
                          setActiveIndex(index)
                        }
                        style={{
                          padding: "5px 12px",
                          cursor: "pointer",
                          background:
                            activeIndex === index
                              ? "#ededed"
                              : "#fff",
                          borderBottom:
                            "1px solid #ddd",
                        }}
                      >
                        {highlightText(
                          customer.accountName,
                          search
                        )}
                      </div>
                    )
                  )
                ) : (
                  <div
                    style={{
                      padding: 10,
                      textAlign: "center",
                    }}
                  >
                    No customer found
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
export default AddCartMobile;




